import React, { PureComponent } from 'react';
import { Flex } from '../../../common/Flex';
import { FlowGrid } from '../../network/flows/FlowGrid';
import { MeasureLoad } from '../../network/flows/MeasureLoad';
import { ExtendedMonitoredTv, MonitoredEvent, MonitoredMeasure } from 'app/model/SOL49';
import MeasureUtils from '../../../../common/util/MeasureUtils';
import { PlusOutlined, SettingFilled, TableOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Button, Select } from 'antd';
import { SelectionLabel } from '../../../common/SelectionLabel';
import { CircleIcon } from '../../../icons/CircleIcon';
import { ToggleButton } from '../../../common/ToggleButton';
import Utils from '../../../../common/util/Utils';
import { SortUtils } from 'app/common/util/SortUtils';
import { ClosablePopover } from '../../../common/ClosablePopover';
import {
    MeasuresSettings,
    MeasuresSettingsContent,
    MeasuresSettingsTitle,
    REGULATION_REASONS,
} from './MeasuresSettings';
import { TimePeriod } from 'app/model/CORE';
import { ModalHandler } from '../../../panels/ModalHandler';
import MonitoredMeasureTable, {
    MONITORED_MEASURE_ACTIONS_COLUMN,
    MONITORED_MEASURE_SELECTED_COLUMN,
    MonitoredMeasureColumn,
    StandardMonitoredMeasureColumn,
} from '../../../table/MonitoredMeasureTable';
import memoizeOne from 'memoize-one';
import { MULTIPLE_SELECTION, MultipleSelection, SortMode } from 'app/common/table/ManagedTable';
import './Measures.less';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { Highlighter } from 'app/common/table/Highlighter';
import { Sorting } from '@devexpress/dx-react-grid';
import { TableUtils } from 'app/common/util/TableUtils';
import { MonitoredEventUtils } from 'app/common/util/MonitoredEventUtils';
import AppConstants from '../../../../common/context/AppConstants';

const REGULATION_LIST_COLUMNS: MonitoredMeasureColumn[] = [
    MONITORED_MEASURE_SELECTED_COLUMN,
    StandardMonitoredMeasureColumn.ID,
    StandardMonitoredMeasureColumn.TV,
    StandardMonitoredMeasureColumn.START,
    StandardMonitoredMeasureColumn.END,
    StandardMonitoredMeasureColumn.DELAY,
    StandardMonitoredMeasureColumn.NORMAL_RATE,
    StandardMonitoredMeasureColumn.PENDING_RATE,
    StandardMonitoredMeasureColumn.WINDOW_WIDTH,
    StandardMonitoredMeasureColumn.STATE,
    StandardMonitoredMeasureColumn.REASON,
    StandardMonitoredMeasureColumn.LFRS,
    StandardMonitoredMeasureColumn.WARNING,
    MONITORED_MEASURE_ACTIONS_COLUMN,
];

const DEFAULT_MONITORED_MEASURES_SORTING: Sorting[] = [{ columnName: 'ID', direction: 'asc' }];

const MONITORED_MEASURES_HIGHLIGHTER: Highlighter<MonitoredMeasure> = {
    highlight(elem: MonitoredMeasure): string {
        return MeasureUtils.getMonitoredMeasureCreationClassname(elem);
    },
};

interface MeasuresProps {
    currentTime: Date;
    monitoredPeriod: TimePeriod;
    measures: Array<MonitoredMeasure>;
    proposalMeasures: Array<string>;
    monitoredEvents?: Array<MonitoredEvent>;
    monitoredTvList: Array<ExtendedMonitoredTv>;
    textFilter: string;

    selectedMeasures?: MonitoredMeasure[];
    onSelectionChange?: (measures: Array<MonitoredMeasure>) => void;
    settings: MeasuresSettings;
    onMeasureSettingsChange(settings: MeasuresSettings): void;
}

interface MeasuresState {
    gridDisplayMode: boolean;
    showOnlyMeasuresWithWarnings: boolean;
    sortBy: 'name' | 'duration' | 'start_time' | 'end_time';

    sorting: Sorting[];
}

export class Measures extends PureComponent<MeasuresProps, MeasuresState> {
    private settingsPopoverRef = React.createRef<ClosablePopover>();

    constructor(props) {
        super(props);
        this.state = {
            gridDisplayMode: true,
            showOnlyMeasuresWithWarnings: false,
            sortBy: 'name',
            // settings: DEFAULT_MEASURES_SETTINGS,
            sorting: DEFAULT_MONITORED_MEASURES_SORTING,
        };
    }

    getFilteredMeasures = memoizeOne(
        (measures: MonitoredMeasure[], textFilter: string, settings: MeasuresSettings, monitoredPeriod: TimePeriod) => {
            return MeasureUtils.getFilteredMeasures(measures, textFilter, settings, monitoredPeriod);
        },
    );

    selection = () => {
        const memoizedSelection = memoizeOne(
            (
                measures: MonitoredMeasure[],
                selectedMeasures: MonitoredMeasure[],
            ): MultipleSelection<MonitoredMeasure> => ({
                selectionMode: MULTIPLE_SELECTION,
                onSelectionChange: this.handleSelectionChange,
                selectedItems: selectedMeasures,
            }),
        );
        return memoizedSelection(this.props.measures, this.props.selectedMeasures);
    };

    handleSettingsChange = (settings: MeasuresSettings) => {
        this.props.onMeasureSettingsChange(settings);
        this.settingsPopoverRef.current.hide();
    };

    handleSelectionChange = (measures: MonitoredMeasure[]) => {
        this.props.onSelectionChange?.(measures);
    };

    handleSelectionUpdate = (measure: MonitoredMeasure, selected: boolean) => {
        const newSelection = selected
            ? Utils.copyAndAddIfNeeded(this.props.selectedMeasures, measure)
            : Utils.copyAndRemoveIfNeeded(this.props.selectedMeasures, measure);
        this.handleSelectionChange(newSelection);
    };

    sortingAlgorithm = () => {
        switch (this.state.sortBy) {
            case 'duration':
                return SortUtils.MONITORED_MEASURE_DURATION_SORTING;
            case 'start_time':
                return SortUtils.MONITORED_MEASURE_START_TIME_SORTING;
            case 'end_time':
                return SortUtils.MONITORED_MEASURE_END_TIME_SORTING;
            case 'name':
            default:
                return SortUtils.MONITORED_MEASURE_ALPHABETICAL_SORTING;
        }
    };

    handleSelectAll = (selection: Array<MonitoredMeasure>) => {
        const newSelection = Utils.copyAndAddAllIfNeeded(this.props.selectedMeasures, selection);
        return () => {
            this.props.onSelectionChange?.(newSelection);
        };
    };

    handleClearSelection = () => {
        this.props.onSelectionChange?.([]);
    };

    handleNewMeasureClick = () => {
        ModalHandler.showRegulationEditor(undefined, undefined);
    };

    switchDisplayMode = () => this.setState({ gridDisplayMode: !this.state.gridDisplayMode });

    renderMeasureSelectionInfo = nbMeasures => (
        <div className={'measure-selection-info'}>
            <span className="detailed-subpanel-title">MEASURES</span>
            <span className="detailed-subpanel-title-info">{nbMeasures}</span>
        </div>
    );

    renderMeasuresGrid = (filteredMeasures: MonitoredMeasure[], selectedMeasureIds: string[]) => {
        return (
            <Flex className="content overflow" grow={false}>
                <FlowGrid columns={AppConstants.nbMeasuresColumns}>
                    {filteredMeasures.sort(this.sortingAlgorithm()).map(monitoredMeasure => {
                        const selected = Utils.contains(selectedMeasureIds, monitoredMeasure.measure.measureId.id);
                        const hasOngoingProposal =
                            this.props.proposalMeasures &&
                            Utils.contains(this.props.proposalMeasures, monitoredMeasure.measure.measureId.id);
                        const location = MeasureUtils.getTvLocation(monitoredMeasure.measure);
                        const monitoredEvents = this.props.monitoredEvents?.filter(
                            event =>
                                (MonitoredEventUtils.isNoteEvent(event) &&
                                    event.measures?.includes(monitoredMeasure.measure.measureId.id)) ||
                                (MonitoredEventUtils.isWeatherEvent(event) &&
                                    event.refLocs?.some(refLoc => Utils.isShallowEquals(refLoc, location.refLocation))),
                        );
                        return (
                            <MeasureLoad
                                key={monitoredMeasure.measure.measureId.id}
                                monitoredMeasure={monitoredMeasure}
                                monitoredEvents={monitoredEvents}
                                currentTime={this.props.currentTime}
                                monitoredTv={this.props.monitoredTvList?.find(
                                    monitoredTv => location.id === monitoredTv.refLocation.id,
                                )}
                                selected={selected}
                                onSelectionChange={selected => this.handleSelectionUpdate(monitoredMeasure, selected)}
                                hasOngoingProposal={hasOngoingProposal}
                            />
                        );
                    })}
                </FlowGrid>
            </Flex>
        );
    };

    onSortingChange = sorting => this.setState({ sorting });

    renderMeasuresTable = (filteredMeasures: MonitoredMeasure[]) => {
        const sorted = TableUtils.sortData(
            filteredMeasures,
            REGULATION_LIST_COLUMNS,
            this.state.sorting,
            null,
            SortUtils.MONITORED_MEASURE_NEW_CREATION_SORTING,
        );
        return (
            <Flex className="content">
                <MonitoredMeasureTable
                    data={sorted}
                    sorting={this.state.sorting}
                    onSortingChange={this.onSortingChange}
                    sortMode={SortMode.EXTERNAL}
                    defaultSorting={DEFAULT_MONITORED_MEASURES_SORTING}
                    highlighter={MONITORED_MEASURES_HIGHLIGHTER}
                    columns={REGULATION_LIST_COLUMNS}
                    columnResizeDisabled={true}
                    selection={this.selection()}
                />
            </Flex>
        );
    };

    private filteredMeasuresTextHint = () => {
        return (
            <Flex direction="vertical" style={{ paddingLeft: '1em' }}>
                <ConditionalRender condition={this.props.settings.activeOnly}>
                    <span>- Measures in active state</span>
                </ConditionalRender>
                <ConditionalRender condition={this.props.settings.monitoredPeriodOnly}>
                    <span>- Measures in monitored period</span>
                </ConditionalRender>
                <ConditionalRender condition={this.state.showOnlyMeasuresWithWarnings}>
                    <span>- Measures with warning</span>
                </ConditionalRender>
                <ConditionalRender condition={Utils.isNotEmptyString(this.props.textFilter)}>
                    <span>- Measures matching text filter ( {this.props.textFilter?.toUpperCase()}* )</span>
                </ConditionalRender>
                <ConditionalRender condition={this.props.settings.reasons.length < REGULATION_REASONS.length}>
                    <span>- Measures with filtered reasons</span>
                </ConditionalRender>
            </Flex>
        );
    };

    render() {
        const filteredMeasures = this.getFilteredMeasures(
            this.props.measures,
            this.props.textFilter,
            this.props.settings,
            this.props.monitoredPeriod,
        ).filter(
            monitoredMeasure => !this.state.showOnlyMeasuresWithWarnings || Utils.isNotEmpty(monitoredMeasure.warnings),
        );
        const nbMeasures = filteredMeasures?.length || 0;
        const nbMeasuresSelected = this.props.selectedMeasures?.length || 0;
        const selectedMeasureIds = this.props.selectedMeasures?.map(
            monitoredMeasure => monitoredMeasure.measure.measureId.id,
        );
        const selectionLabelVisible = nbMeasuresSelected > 0;
        const nbMeasuresWithWarnings =
            filteredMeasures?.filter(monitoredMeasure => Utils.isNotEmpty(monitoredMeasure.warnings)).length || 0;
        const nbHiddenMeasures = this.props.measures.length - filteredMeasures.length;

        return (
            <Flex direction="vertical" className="measures overflow detailed-subpanel">
                <Flex className="detailed-subpanel-header" size="2em" style={{ margin: 0 }}>
                    <Flex className="detailed-subpanel-header-left" size="50%">
                        <SelectionLabel
                            visible={selectionLabelVisible}
                            title={`${nbMeasuresSelected} measure${nbMeasuresSelected > 1 ? 's' : ''} selected`}
                            subTitle={`${nbMeasures} in total`}
                            onSelectAll={this.handleSelectAll(filteredMeasures)}
                            onClearSelection={this.handleClearSelection}
                            children={this.renderMeasureSelectionInfo(nbMeasures)}
                        />
                        <Flex className="new-measure">
                            <Button icon={<PlusOutlined />} onClick={this.handleNewMeasureClick}>
                                NEW MEASURE
                            </Button>
                        </Flex>
                    </Flex>
                    <Flex className="detailed-subpanel-header-right" size="50%">
                        <ToggleButton
                            icon={<CircleIcon className="warning-icon" />}
                            checked={this.state.showOnlyMeasuresWithWarnings}
                            onChange={checked => this.setState({ showOnlyMeasuresWithWarnings: checked })}>
                            Warnings ({nbMeasuresWithWarnings})
                        </ToggleButton>
                        <ConditionalRender condition={this.state.gridDisplayMode}>
                            <Select
                                className="measure-sortby"
                                value={this.state.sortBy}
                                onChange={value => this.setState({ sortBy: value })}>
                                <Select.Option value="name">Name</Select.Option>
                                <Select.Option value="duration">Duration</Select.Option>
                                <Select.Option value="start_time">Start Time</Select.Option>
                                <Select.Option value="end_time">End Time</Select.Option>
                            </Select>
                        </ConditionalRender>
                        <span className="detailed-subpanel-header-right" style={{ display: 'flex' }}>
                            <ConditionalRender condition={this.state.gridDisplayMode}>
                                <UnorderedListOutlined onClick={this.switchDisplayMode} />
                            </ConditionalRender>
                            <ConditionalRender condition={!this.state.gridDisplayMode}>
                                <TableOutlined onClick={this.switchDisplayMode} />
                            </ConditionalRender>
                            <ClosablePopover
                                key="settings-action"
                                ref={this.settingsPopoverRef}
                                trigger="click"
                                placement="bottom"
                                title={<MeasuresSettingsTitle />}
                                content={
                                    <MeasuresSettingsContent
                                        settings={this.props.settings}
                                        onChange={this.handleSettingsChange}
                                        onClose={() => this.settingsPopoverRef.current.hide()}
                                    />
                                }>
                                <SettingFilled onClick={() => this.settingsPopoverRef.current.show()} />
                            </ClosablePopover>
                        </span>
                    </Flex>
                </Flex>
                <Flex direction={'vertical'} size={'2em'} style={{ alignItems: 'flex-end' }}>
                    <ConditionalRender condition={nbHiddenMeasures > 0}>
                        <Flex
                            direction="vertical"
                            style={{
                                backgroundColor: '#3A3A3A',
                                color: '#EEEEEE',
                                padding: '0.5em',
                                borderRadius: '0.5em',
                                lineHeight: '1.1em',
                                fontSize: '1.1em',
                                marginBottom: '1em',
                            }}>
                            <Flex>{`${nbHiddenMeasures} ${nbHiddenMeasures > 1 ? 'measures are' : 'measure is'} filtered, displaying only:`}</Flex>
                            {this.filteredMeasuresTextHint()}
                        </Flex>
                    </ConditionalRender>
                </Flex>
                <ConditionalRender condition={this.state.gridDisplayMode}>
                    {this.renderMeasuresGrid(filteredMeasures, selectedMeasureIds)}
                </ConditionalRender>
                <ConditionalRender condition={!this.state.gridDisplayMode}>
                    {this.renderMeasuresTable(filteredMeasures)}
                </ConditionalRender>
            </Flex>
        );
    }
}
